import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Nav, Navbar, Offcanvas } from 'react-bootstrap';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { useMembership } from '@/shared/contexts/MembershipContext';
import useCapability from '@/shared/hooks/useCapability';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';
import {
    getEcosystemDestinationUrl,
    getPlatformApexOrigin,
} from '@/shared/utils/ecosystemRouting';
import useFullAuth from '@/features/auth/hooks/useFullAuth';
import {
    getTenantLogoUrl,
    RILE_HORIZONTAL_LOGO_URL,
} from '@/shared/utils/brandAssets';
import './NavBar.css';

// Apex (platform-mode) Explore dropdown link set. Only links to platform-apex
// routes: the tenant-only routes (/directory, /opportunities, etc.) would
// resolve to the catch-all 404 on the apex.
const APEX_EXPLORE_LINKS = [
    { to: '/about', label: 'About RILE Connect' },
    { to: '/mission', label: 'Mission' },
    { to: '/ecosystems', label: 'Ecosystems' },
    { to: '/policies', label: 'Policies' },
    { to: '/contact', label: 'Contact' },
];

// Tenant Explore dropdown link set. React Router's basename auto-prepends the
// active tenant prefix in path mode (so `/directory` becomes `/costem/directory`).
//
// Per-link capability gates (see `requires`), in three forms:
//   - omitted (or `null`) -- always show the link
//   - a string            -- show only when that one capability is enabled
//   - an array            -- show when ANY of the listed capabilities is
//                            enabled (not all of them)
//
// Connections uses the array form: the link shows when either `mentoring`
// or `discoverPartners` is enabled. `ConnectionsIndexRedirect` in
// `features/connections/ConnectionsPage.jsx` applies the same either-or
// rule when picking a landing tab.
//
// Directory is intentionally NOT gated on `networkGraph`: the card view
// stays useful even when the graph tab is disabled. The graph surfaces
// inside Directory are gated separately at the component level.
const TENANT_EXPLORE_LINKS = [
    { to: '/about', label: 'About Us' },
    { to: '/mission', label: 'Mission' },
    { to: '/directory', label: 'Ecosystem' },
    { to: '/ecosystem', label: 'Insights', requires: 'ecosystemAnalytics' },
    {
        to: '/connections',
        label: 'Connections',
        requires: ['mentoring', 'discoverPartners'],
    },
    { to: '/opportunities', label: 'Opportunities', requires: 'opportunities' },
    { to: '/assistant', label: 'AI Assistant', requires: 'aiAssistant' },
    { to: '/policies', label: 'Policies' },
    { to: '/contact', label: 'Contact' },
];

export default function NavBar() {
    const [expanded, setExpanded] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [showMobileAccountSection] = useState(true);
    const dropdownRefs = useRef({});

    const { logout, user } = useAuth();
    const { user: fullyAuthenticatedUser } = useFullAuth();
    const { ecosystem, isPlatform } = useEcosystem();
    const ecosystemApi = useEcosystemApi();
    // Post-P4a, `User.role` is `'user' | 'platformAdmin'` only: the legacy
    // `'admin'` value no longer exists. Per-ecosystem admin authority lives on
    // EcosystemMembership.role in the active tenant.
    const { isMember, role: membershipRole } = useMembership();
    const isEcosystemAdmin = isMember && membershipRole === 'admin';
    const isPlatformAdmin = fullyAuthenticatedUser?.role === 'platformAdmin';
    const navigate = useNavigate();
    const location = useLocation();

    // Per-tenant capability flags read up-front so the filter below stays
    // pure JS (no inline hook calls inside the .filter callback). Apex
    // mode returns true from every flag, so the same hook calls work in
    // both modes: filtering is then a tenant-only effect in practice.
    const capMentoring = useCapability('mentoring');
    const capOpportunities = useCapability('opportunities');
    const capDiscoverPartners = useCapability('discoverPartners');
    const capEcosystemAnalytics = useCapability('ecosystemAnalytics');
    const capAiAssistant = useCapability('aiAssistant');
    const capabilityFlags = {
        mentoring: capMentoring,
        opportunities: capOpportunities,
        discoverPartners: capDiscoverPartners,
        ecosystemAnalytics: capEcosystemAnalytics,
        aiAssistant: capAiAssistant,
    };

    // Adaptive Explore link set + brand. On the apex, the tenant-only routes
    // would 404; on a tenant, the apex-only routes also belong in the menu
    // for navigating back to the platform (Ecosystems link).
    const baseExploreLinks = isPlatform
        ? APEX_EXPLORE_LINKS
        : TENANT_EXPLORE_LINKS;
    // Filter links by their `requires` declaration. See the three accepted
    // forms documented on TENANT_EXPLORE_LINKS above; an array is an ANY
    // check, not an ALL check.
    const exploreLinks = baseExploreLinks.filter((link) => {
        if (!link.requires) return true;
        if (Array.isArray(link.requires)) {
            return link.requires.some((cap) => capabilityFlags[cap]);
        }
        return capabilityFlags[link.requires];
    });
    const brandLabel = isPlatform
        ? 'RILE Connect'
        : ecosystem?.name || 'RILE Connect';
    const brandLogoUrl = getTenantLogoUrl(ecosystem);
    // The horizontal lockup has "RILE Connect" baked into the image, so it
    // only belongs on the actual platform apex (isPlatform). A tenant renders
    // its own logo (or no image at all, just its name) - the RILE mark must
    // never stand in for a tenant's own missing logo.

    // The user's own page differs by tenancy tier (#261): Account at the
    // platform apex (identity on the User document), Profile inside a tenant
    // (per-ecosystem state on EcosystemMembership). Two distinct pages, so the
    // menu entry's label and target both follow the active context.
    //
    // Note these stay relative Link/navigate targets, unlike the Dashboard and
    // Platform Admin entries: each destination lives at the SAME tenancy level
    // the user is already on, so the router basename resolves them correctly
    // and no full-page navigation is needed.
    const userPagePath = isPlatform ? '/account' : '/profile';
    const userPageLabel = isPlatform ? 'Account' : 'Profile';

    // --- Ecosystem switcher ---
    // The signed-in user's active memberships, fetched lazily so the platform
    // apex and signed-out paths don't pay the cost. Only rendered when the
    // user has 2+ memberships (1 = nothing to switch to; 0 = nothing to show).
    const [memberships, setMemberships] = useState([]);
    const [membershipsLoaded, setMembershipsLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // Skip when signed out: the endpoint requires auth.
        if (!fullyAuthenticatedUser) {
            setMemberships([]);
            setMembershipsLoaded(false);
            return undefined;
        }
        (async () => {
            try {
                const data = await ecosystemApi.fetchMyMemberships();
                if (cancelled) return;
                const list = Array.isArray(data?.memberships)
                    ? data.memberships
                    : [];
                setMemberships(list);
                setMembershipsLoaded(true);
            } catch {
                // Switcher just hides on failure, not a blocking UI.
                if (!cancelled) {
                    setMemberships([]);
                    setMembershipsLoaded(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fullyAuthenticatedUser, ecosystemApi]);

    // Switcher renders only when the user has 2+ active memberships.
    const showEcosystemSwitcher = membershipsLoaded && memberships.length >= 2;

    const handleSwitchEcosystem = (eco) => {
        // Cross-tenant navigation must be a FULL navigation so EcosystemProvider
        // re-bootstraps (its boot useEffect has empty deps). Same constraint as
        // EcosystemDirectory's Visit button.
        const url = getEcosystemDestinationUrl(eco);
        if (url) {
            window.location.href = url;
        }
        setActiveDropdown(null);
        setExpanded(false);
    };

    // Use either fully authenticated user OR basic user for mobile display
    const isLoggedIn = fullyAuthenticatedUser || user;

    // Show "Continue Tour" when user has an incomplete tour and is not on dashboard
    const showContinueTour =
        fullyAuthenticatedUser &&
        !fullyAuthenticatedUser.hasSeenTour &&
        location.pathname !== '/dashboard';

    const handleNavClick = () => {
        setExpanded(false);
        setActiveDropdown(null);
    };

    const toggleDropdown = (dropdownName) => {
        if (activeDropdown === dropdownName) {
            setActiveDropdown(null);
        } else {
            setActiveDropdown(dropdownName);
        }
    };

    const handleDropdownToggle = (dropdownName) => {
        toggleDropdown(dropdownName);
    };

    const handleDropdownKeyDown = (e, dropdownName) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown(dropdownName);
        }
    };

    const handleLogout = async () => {
        await logout();
        setActiveDropdown(null);
        navigate('/');
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeDropdown) {
                const isClickInsideAnyDropdown = Object.values(
                    dropdownRefs.current
                ).some((ref) => ref && ref.contains(event.target));

                if (!isClickInsideAnyDropdown) {
                    setActiveDropdown(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [activeDropdown]);

    // Close dropdown when scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (activeDropdown) {
                setActiveDropdown(null);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeDropdown]);

    // Close dropdown/menu when pressing ESC key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                if (activeDropdown) {
                    setActiveDropdown(null);
                }
                if (expanded) {
                    setExpanded(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [activeDropdown, expanded]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 992) {
                if (expanded) {
                    setExpanded(false);
                }
            }
            if (window.innerWidth < 992) {
                if (activeDropdown && activeDropdown !== 'profile') {
                    setActiveDropdown(null);
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [expanded, activeDropdown]);

    return (
        <section id="navbar">
            <Navbar
                expanded={expanded}
                onToggle={() => setExpanded(!expanded)}
                className="bg-body-tertiary"
                expand="lg"
                bg="light"
            >
                <Container>
                    <Navbar.Brand
                        as={Link}
                        to="/"
                        className="d-flex align-items-center gap-2"
                        onClick={handleNavClick}
                    >
                        {isPlatform ? (
                            <img
                                src={RILE_HORIZONTAL_LOGO_URL}
                                alt={brandLabel}
                                className="navbar-brand-logo-horizontal"
                            />
                        ) : (
                            <>
                                {brandLogoUrl && (
                                    <img
                                        src={brandLogoUrl}
                                        alt={`${brandLabel} logo`}
                                        className="navbar-brand-logo"
                                    />
                                )}
                                {/* The brand is intentionally NOT a heading. The
                                    page's single <h1> belongs to the page hero;
                                    rendering the brand as an <h1> here produced
                                    two h1s on every page. A <span> styled
                                    identically keeps the visual weight without
                                    the heading semantics. */}
                                <span className="fs-3 text-primary fw-semibold mb-0">
                                    {brandLabel}
                                </span>
                            </>
                        )}
                    </Navbar.Brand>

                    {/* Mobile hamburger */}
                    <Navbar.Toggle
                        aria-controls="offcanvas-navbar"
                        className="d-lg-none border-0"
                        aria-label="Toggle navigation"
                    />

                    {/* Desktop Navigation */}
                    <Navbar.Collapse
                        id="right-aligned"
                        className="d-none d-lg-flex justify-content-end"
                    >
                        <Nav className="ms-auto align-items-lg-center">
                            {/* Continue Tour - shown left of Explore when tour is incomplete */}
                            {showContinueTour && (
                                <Link
                                    to="/dashboard"
                                    className="btn btn-accent btn-sm fw-medium rounded-pill d-none d-lg-inline-flex align-items-center continue-tour-btn"
                                    onClick={(e) => {
                                        if (!isPlatform) {
                                            e.preventDefault();
                                            window.location.href = `${getPlatformApexOrigin()}/dashboard`;
                                        }
                                        handleNavClick();
                                    }}
                                >
                                    Continue Tour
                                </Link>
                            )}

                            {/* Explore Dropdown */}
                            <div
                                className={`nav-dropdown position-relative d-flex align-items-center ${activeDropdown === 'explore' ? 'show' : ''}`}
                                ref={(el) =>
                                    (dropdownRefs.current.explore = el)
                                }
                            >
                                <div
                                    className="nav-dropdown-toggle d-flex align-items-center gap-2 text-primary fw-medium text-decoration-none rounded-pill bg-transparent border-0 fs-4"
                                    onClick={() =>
                                        handleDropdownToggle('explore')
                                    }
                                    onKeyDown={(e) =>
                                        handleDropdownKeyDown(e, 'explore')
                                    }
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={activeDropdown === 'explore'}
                                >
                                    Explore{' '}
                                    {activeDropdown === 'explore' ? '▲' : '▼'}
                                </div>
                                <div className="nav-dropdown-menu position-absolute bg-body border rounded-3 shadow-sm d-flex flex-column gap-2 p-3">
                                    {exploreLinks.map((link) => (
                                        <Link
                                            key={link.to}
                                            to={link.to}
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Account Section - Logged Out */}
                            {!fullyAuthenticatedUser && !user ? (
                                <>
                                    <Link
                                        to="/login"
                                        className="btn btn-primary nav-btn-auth mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5"
                                        onClick={handleNavClick}
                                    >
                                        Login
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="btn btn-primary nav-btn-auth mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5"
                                        onClick={handleNavClick}
                                    >
                                        Sign Up
                                    </Link>
                                </>
                            ) : !fullyAuthenticatedUser && user ? (
                                <button
                                    onClick={handleLogout}
                                    className="btn btn-primary nav-btn-auth mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5"
                                >
                                    Log Out
                                </button>
                            ) : (
                                <div
                                    className={`nav-dropdown profile-dropdown position-relative d-none d-lg-flex align-items-center ${activeDropdown === 'profile' ? 'show' : ''}`}
                                    ref={(el) =>
                                        (dropdownRefs.current.profile = el)
                                    }
                                >
                                    <div
                                        className="profile-icon-button d-flex align-items-center justify-content-center text-primary bg-transparent border-0 p-0"
                                        onClick={() =>
                                            handleDropdownToggle('profile')
                                        }
                                        onKeyDown={(e) =>
                                            handleDropdownKeyDown(e, 'profile')
                                        }
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Profile menu"
                                        aria-expanded={
                                            activeDropdown === 'profile'
                                        }
                                    >
                                        👤
                                    </div>
                                    <div className="nav-dropdown-menu profile-menu position-absolute bg-body border rounded-3 shadow-sm d-flex flex-column gap-2 p-2">
                                        <Link
                                            to="/dashboard"
                                            className="btn btn-accent fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                            onClick={(e) => {
                                                if (!isPlatform) {
                                                    e.preventDefault();
                                                    window.location.href = `${getPlatformApexOrigin()}/dashboard`;
                                                }
                                                handleNavClick();
                                            }}
                                        >
                                            Dashboard
                                        </Link>
                                        <Link
                                            to={userPagePath}
                                            className="btn btn-accent fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                            onClick={handleNavClick}
                                        >
                                            {userPageLabel}
                                        </Link>
                                        {showEcosystemSwitcher && (
                                            <div className="ecosystem-switcher d-flex flex-column gap-1 mt-1 pt-2 border-top">
                                                <span className="small text-muted text-center fw-semibold ecosystem-switcher-label">
                                                    Switch Ecosystem
                                                </span>
                                                {memberships.map((m) => {
                                                    const eco = m.ecosystem;
                                                    const isCurrent =
                                                        ecosystem &&
                                                        ecosystem.slug ===
                                                            eco.slug;
                                                    return (
                                                        <button
                                                            key={eco.slug}
                                                            type="button"
                                                            onClick={() =>
                                                                handleSwitchEcosystem(
                                                                    eco
                                                                )
                                                            }
                                                            disabled={isCurrent}
                                                            className="btn btn-outline-primary fw-medium d-block w-100 rounded-pill text-center profile-menu-btn fs-6"
                                                            aria-current={
                                                                isCurrent
                                                                    ? 'true'
                                                                    : undefined
                                                            }
                                                        >
                                                            {eco.shortName ||
                                                                eco.name}
                                                            {isCurrent
                                                                ? ' (current)'
                                                                : ''}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {isEcosystemAdmin && (
                                            <Link
                                                to="/admin"
                                                className="btn btn-accent fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                                onClick={handleNavClick}
                                            >
                                                Admin
                                            </Link>
                                        )}
                                        {isPlatformAdmin && (
                                            <Link
                                                to="/platform-admin"
                                                className="btn btn-accent fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                                onClick={(e) => {
                                                    if (!isPlatform) {
                                                        e.preventDefault();
                                                        window.location.href = `${getPlatformApexOrigin()}/platform-admin`;
                                                    }
                                                    handleNavClick();
                                                }}
                                            >
                                                Platform Admin
                                            </Link>
                                        )}
                                        <button
                                            onClick={handleLogout}
                                            className="btn btn-primary fw-medium d-block w-100 rounded-pill text-center profile-menu-btn fs-5"
                                        >
                                            Log Out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </Nav>
                    </Navbar.Collapse>

                    {/* Mobile Offcanvas */}
                    <Navbar.Offcanvas
                        id="offcanvas-navbar"
                        aria-labelledby="offcanvas-navbar-label"
                        placement="end"
                        className="d-lg-none w-100 mw-100 border-0"
                    >
                        <Offcanvas.Header closeButton className="border-bottom">
                            <Offcanvas.Title>{brandLabel}</Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body className="bg-primary">
                            {/* Mobile Account Section */}
                            {isLoggedIn && showMobileAccountSection && (
                                <div className="mobile-account-section d-flex flex-column gap-1 p-3 bg-body border rounded-3 shadow-sm mb-3">
                                    <div className="d-flex flex-column gap-2 mobile-account-buttons">
                                        {fullyAuthenticatedUser && (
                                            <>
                                                {showContinueTour && (
                                                    <button
                                                        onClick={() => {
                                                            if (isPlatform) {
                                                                navigate(
                                                                    '/dashboard'
                                                                );
                                                            } else {
                                                                window.location.href = `${getPlatformApexOrigin()}/dashboard`;
                                                            }
                                                            setExpanded(false);
                                                        }}
                                                        className="btn btn-accent fw-medium rounded-pill w-100 continue-tour-btn"
                                                    >
                                                        Continue Tour
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        if (isPlatform) {
                                                            navigate(
                                                                '/dashboard'
                                                            );
                                                        } else {
                                                            window.location.href = `${getPlatformApexOrigin()}/dashboard`;
                                                        }
                                                        setExpanded(false);
                                                    }}
                                                    className="btn btn-accent fw-medium rounded-pill w-100"
                                                >
                                                    Dashboard
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        navigate(userPagePath);
                                                        setExpanded(false);
                                                    }}
                                                    className="btn btn-accent fw-medium rounded-pill w-100"
                                                >
                                                    {userPageLabel}
                                                </button>
                                                {isEcosystemAdmin && (
                                                    <button
                                                        onClick={() => {
                                                            navigate('/admin');
                                                            setExpanded(false);
                                                        }}
                                                        className="btn btn-accent fw-medium rounded-pill w-100"
                                                    >
                                                        Admin
                                                    </button>
                                                )}
                                                {isPlatformAdmin && (
                                                    <button
                                                        onClick={() => {
                                                            if (isPlatform) {
                                                                navigate(
                                                                    '/platform-admin'
                                                                );
                                                            } else {
                                                                window.location.href = `${getPlatformApexOrigin()}/platform-admin`;
                                                            }
                                                            setExpanded(false);
                                                        }}
                                                        className="btn btn-accent fw-medium rounded-pill w-100"
                                                    >
                                                        Platform Admin
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        <button
                                            onClick={() => {
                                                handleLogout();
                                                setExpanded(false);
                                            }}
                                            className="btn btn-primary fw-medium rounded-pill w-100"
                                        >
                                            Log Out
                                        </button>
                                    </div>
                                </div>
                            )}

                            <Nav className="flex-column">
                                <div className="p-3 bg-body border rounded-3 shadow-sm mb-3">
                                    {exploreLinks.map((link) => (
                                        <Nav.Link
                                            key={link.to}
                                            as={Link}
                                            to={link.to}
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            {link.label}
                                        </Nav.Link>
                                    ))}
                                </div>

                                {/* Mobile ecosystem switcher: same gating as
                                 * the desktop dropdown: render only when the
                                 * user has 2+ active memberships. */}
                                {showEcosystemSwitcher && (
                                    <div className="p-3 bg-body border rounded-3 shadow-sm mb-3 ecosystem-switcher-mobile">
                                        <h6 className="text-primary fw-semibold mb-2">
                                            Switch Ecosystem
                                        </h6>
                                        <div className="d-flex flex-column gap-2">
                                            {memberships.map((m) => {
                                                const eco = m.ecosystem;
                                                const isCurrent =
                                                    ecosystem &&
                                                    ecosystem.slug === eco.slug;
                                                return (
                                                    <button
                                                        key={eco.slug}
                                                        type="button"
                                                        onClick={() =>
                                                            handleSwitchEcosystem(
                                                                eco
                                                            )
                                                        }
                                                        disabled={isCurrent}
                                                        className="btn btn-outline-primary rounded-pill w-100"
                                                        aria-current={
                                                            isCurrent
                                                                ? 'true'
                                                                : undefined
                                                        }
                                                    >
                                                        {eco.shortName ||
                                                            eco.name}
                                                        {isCurrent
                                                            ? ' (current)'
                                                            : ''}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Auth Buttons - Only when logged out */}
                                {!isLoggedIn && (
                                    <div className="p-3 bg-body border rounded-3 shadow-sm mb-3">
                                        <div className="d-flex flex-column gap-2 mobile-auth-buttons">
                                            <Link
                                                to="/login"
                                                className="btn btn-primary w-100 rounded-pill"
                                                onClick={handleNavClick}
                                            >
                                                Login
                                            </Link>
                                            <Link
                                                to="/signup"
                                                className="btn btn-primary w-100 rounded-pill"
                                                onClick={handleNavClick}
                                            >
                                                Sign Up
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </Nav>
                        </Offcanvas.Body>
                    </Navbar.Offcanvas>
                </Container>
            </Navbar>
        </section>
    );
}
